import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, BookOpen, Microscope, MapPin, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import AnimatedSection from '../components/AnimatedSection';
import { useLanguage } from '../i18n/pageTranslations';

const ElDoctor = () => {
  const lang = useLanguage();

  const texts = {
    es: {
      eyebrow: 'Sobre el Dr. Norro',
      heroTitle1: 'Más de tres décadas transformando vidas con',
      heroTitle2: 'precisión y arte',
      biography: 'Biografía y Formación',
      bioParagraph1: 'El Dr. Ángel José Norro Ruiz es especialista en Cirugía Plástica, Estética y Reparadora con más de <strong>35 años de trayectoria clínica ininterrumpida</strong>. Desarrolla su actividad en Marbella, referente internacional de la medicina privada de alto nivel en el sur de Europa.',
      bioParagraph2: 'Fundador del protocolo <strong>NewLip®</strong> —registrado ante la OEPM— para el rejuvenecimiento perioral, y titular de la marca <strong>Rinomodelación®</strong>. Combina actividad clínica de alto volumen con el desarrollo de nuevas aplicaciones terapéuticas.',
      bioParagraph3: 'Director científico de <strong>Metasigna</strong>, plataforma biotecnológica de oncología metabólica que desarrolla conjugados farmacológicos escindibles bajo las marcas Clivamed® y Warcliv®.',
      expertise: 'Áreas de Expertise',
      expertiseItems: [
        'Cirugía facial · Lifting · Blefaroplastia · Rinoplastia · Otoplastia',
        'Cirugía corporal · Mamas · Abdominoplastia · Postbariátrica · Liposucción',
        'Medicina estética avanzada · Rinomodelación® · Endolifting · Armonización',
        'Cirugía dermatológica y reparadora',
        'Hipnosis clínica perioperatoria',
        'Valoración del daño corporal — peritaje médico-legal',
      ],
      philosophy: 'Filosofía y Valores',
      quote: '"La cirugía plástica no trata de cambiar quién es usted. Trata de devolver la armonía entre cómo se siente y cómo se ve."',
      philosophyDesc: 'Cada paciente es único. Cada plan quirúrgico es individualizado. El objetivo no es la transformación radical, sino la restauración de la armonía natural que el tiempo o las circunstancias han alterado.',
      innovation: 'Innovación',
      trademarks: 'Marcas Registradas OEPM',
      newlipDesc: 'Protocolo exclusivo de rejuvenecimiento panfacial, perioral o periocular. Técnica desarrollada y registrada por el Dr. Norro para resultados naturales en la zona del labio y el tercio inferior facial.',
      rinoDesc: 'Técnica no quirúrgica exclusiva para la corrección de la nariz mediante rellenos de ácido hialurónico. Pionero en España desde 2007. Resultados inmediatos, reversibles, sin anestesia general.',
      seeMore: 'Ver más',
      metasigna: 'Metasigna',
      scientificActivity: 'Actividad Científica',
      metasignaDesc1: 'Director científico de <strong>Metasigna</strong>, plataforma biotecnológica de oncología metabólica que desarrolla conjugados farmacológicos escindibles.',
      metasignaDesc2: 'Desarrollo de las marcas <strong>Clivamed®</strong> y <strong>Warcliv®</strong> para aplicaciones terapéuticas de nueva generación.',
      teachingTitle: 'Docencia y Divulgación',
      lecturesTitle: 'Ponencias y Conferencias',
      lecturesPara1: 'El Dr. Norro es <strong>ponente habitual</strong> en congresos nacionales e internacionales de cirugía plástica, medicina estética y técnicas de rejuvenecimiento facial.',
      lecturesPara2: 'Su experiencia de más de tres décadas y el desarrollo de técnicas propias como <strong>NewLip®</strong> y <strong>Rinomodelación®</strong> le han convertido en referente formativo para profesionales de toda Europa y Latinoamérica.',
      lecturesPara3: 'Director de la <strong>NewLip Academy</strong>, donde imparte formación especializada a médicos de diversas disciplinas en técnicas avanzadas de medicina estética facial.',
      clinicCenters: 'Clínica y Centros',
      clinicDesc: 'El Dr. Norro desarrolla su actividad clínica en Marbella, trabajando en diversos hospitales y clínicas de referencia en la Costa del Sol.',
      clinicAlameda: 'Clínica Estética Alameda',
      viewAllCenters: 'Ver todos los centros',
      credentials: 'Credenciales',
      accreditations: 'Acreditaciones y Certificaciones',
      accreditationsDesc: 'Miembro de las principales sociedades científicas nacionales e internacionales de cirugía plástica y medicina estética.',
      viewAccreditations: 'Ver acreditaciones',
      wantToKnowMore: '¿Desea conocer más?',
      requestConsultation: 'Solicite una consulta de valoración personalizada con el Dr. Norro.',
      requestConsult: 'Solicitar consulta',
    },
    en: {
      eyebrow: 'About Dr. Norro',
      heroTitle1: 'Over three decades transforming lives with',
      heroTitle2: 'precision and art',
      biography: 'Biography and Training',
      bioParagraph1: 'Dr. Ángel José Norro Ruiz is a specialist in Plastic, Aesthetic and Reconstructive Surgery with over <strong>35 years of uninterrupted clinical experience</strong>. He practices in Marbella, an international reference for high-level private medicine in southern Europe.',
      bioParagraph2: 'Founder of the <strong>NewLip®</strong> protocol —registered with OEPM— for perioral rejuvenation, and owner of the <strong>Rhinomodeling®</strong> trademark. He combines high-volume clinical activity with the development of new therapeutic applications.',
      bioParagraph3: 'Scientific director of <strong>Metasigna</strong>, a biotechnological platform for metabolic oncology developing cleavable pharmaceutical conjugates under the Clivamed® and Warcliv® brands.',
      expertise: 'Areas of Expertise',
      expertiseItems: [
        'Facial surgery · Facelift · Blepharoplasty · Rhinoplasty · Otoplasty',
        'Body surgery · Breast · Abdominoplasty · Post-bariatric · Liposuction',
        'Advanced aesthetic medicine · Rhinomodeling® · Endolifting · Harmonization',
        'Dermatological and reconstructive surgery',
        'Perioperative clinical hypnosis',
        'Bodily damage assessment — medical-legal expertise',
      ],
      philosophy: 'Philosophy and Values',
      quote: '"Plastic surgery is not about changing who you are. It\'s about restoring harmony between how you feel and how you look."',
      philosophyDesc: 'Each patient is unique. Each surgical plan is individualized. The goal is not radical transformation, but the restoration of natural harmony that time or circumstances have altered.',
      innovation: 'Innovation',
      trademarks: 'OEPM Registered Trademarks',
      newlipDesc: 'Exclusive panfacial, perioral or periocular rejuvenation protocol. Technique developed and registered by Dr. Norro for natural results in the lip area and lower third of the face.',
      rinoDesc: 'Exclusive non-surgical technique for nose correction using hyaluronic acid fillers. Pioneer in Spain since 2007. Immediate, reversible results without general anesthesia.',
      seeMore: 'See more',
      metasigna: 'Metasigna',
      scientificActivity: 'Scientific Activity',
      metasignaDesc1: 'Scientific director of <strong>Metasigna</strong>, a biotechnological platform for metabolic oncology developing cleavable pharmaceutical conjugates.',
      metasignaDesc2: 'Development of <strong>Clivamed®</strong> and <strong>Warcliv®</strong> brands for next-generation therapeutic applications.',
      teachingTitle: 'Teaching and Outreach',
      lecturesTitle: 'Lectures and Conferences',
      lecturesPara1: 'Dr. Norro is a <strong>regular speaker</strong> at national and international congresses on plastic surgery, aesthetic medicine and facial rejuvenation techniques.',
      lecturesPara2: 'His experience of over three decades and the development of his own techniques such as <strong>NewLip®</strong> and <strong>Rhinomodeling®</strong> have made him a training reference for professionals throughout Europe and Latin America.',
      lecturesPara3: 'Director of <strong>NewLip Academy</strong>, where he provides specialized training to physicians from various disciplines in advanced facial aesthetic medicine techniques.',
      clinicCenters: 'Clinic and Centers',
      clinicDesc: 'Dr. Norro practices in Marbella, working in various reference hospitals and clinics on the Costa del Sol.',
      clinicAlameda: 'Alameda Aesthetic Clinic',
      viewAllCenters: 'View all centers',
      credentials: 'Credentials',
      accreditations: 'Accreditations and Certifications',
      accreditationsDesc: 'Member of the leading national and international scientific societies for plastic surgery and aesthetic medicine.',
      viewAccreditations: 'View accreditations',
      wantToKnowMore: 'Want to know more?',
      requestConsultation: 'Request a personalized assessment consultation with Dr. Norro.',
      requestConsult: 'Request consultation',
    },
    fr: {
      eyebrow: 'À propos du Dr. Norro',
      heroTitle1: 'Plus de trois décennies à transformer des vies avec',
      heroTitle2: 'précision et art',
      biography: 'Biographie et Formation',
      bioParagraph1: 'Le Dr. Ángel José Norro Ruiz est spécialiste en Chirurgie Plastique, Esthétique et Réparatrice avec plus de <strong>35 ans d\'expérience clinique ininterrompue</strong>. Il exerce à Marbella, référence internationale de la médecine privée haut de gamme dans le sud de l\'Europe.',
      bioParagraph2: 'Fondateur du protocole <strong>NewLip®</strong> —enregistré auprès de l\'OEPM— pour le rajeunissement périoral, et titulaire de la marque <strong>Rhinomodelage®</strong>. Il combine une activité clinique intensive avec le développement de nouvelles applications thérapeutiques.',
      bioParagraph3: 'Directeur scientifique de <strong>Metasigna</strong>, plateforme biotechnologique d\'oncologie métabolique développant des conjugués pharmaceutiques clivables sous les marques Clivamed® et Warcliv®.',
      expertise: 'Domaines d\'Expertise',
      expertiseItems: [
        'Chirurgie faciale · Lifting · Blépharoplastie · Rhinoplastie · Otoplastie',
        'Chirurgie corporelle · Seins · Abdominoplastie · Post-bariatrique · Liposuccion',
        'Médecine esthétique avancée · Rhinomodelage® · Endolifting · Harmonisation',
        'Chirurgie dermatologique et réparatrice',
        'Hypnose clinique périopératoire',
        'Évaluation du dommage corporel — expertise médico-légale',
      ],
      philosophy: 'Philosophie et Valeurs',
      quote: '"La chirurgie plastique ne consiste pas à changer qui vous êtes. Elle consiste à restaurer l\'harmonie entre ce que vous ressentez et ce que vous voyez."',
      philosophyDesc: 'Chaque patient est unique. Chaque plan chirurgical est individualisé. L\'objectif n\'est pas la transformation radicale, mais la restauration de l\'harmonie naturelle que le temps ou les circonstances ont altérée.',
      innovation: 'Innovation',
      trademarks: 'Marques Déposées OEPM',
      newlipDesc: 'Protocole exclusif de rajeunissement panfacial, périoral ou périoculaire. Technique développée et enregistrée par le Dr. Norro pour des résultats naturels dans la zone des lèvres et du tiers inférieur du visage.',
      rinoDesc: 'Technique non chirurgicale exclusive pour la correction du nez par des fillers d\'acide hyaluronique. Pionnier en Espagne depuis 2007. Résultats immédiats, réversibles, sans anesthésie générale.',
      seeMore: 'Voir plus',
      metasigna: 'Metasigna',
      scientificActivity: 'Activité Scientifique',
      metasignaDesc1: 'Directeur scientifique de <strong>Metasigna</strong>, plateforme biotechnologique d\'oncologie métabolique développant des conjugués pharmaceutiques clivables.',
      metasignaDesc2: 'Développement des marques <strong>Clivamed®</strong> et <strong>Warcliv®</strong> pour des applications thérapeutiques de nouvelle génération.',
      teachingTitle: 'Enseignement et Diffusion',
      lecturesTitle: 'Conférences et Présentations',
      lecturesPara1: 'Le Dr. Norro est un <strong>conférencier régulier</strong> lors de congrès nationaux et internationaux de chirurgie plastique, médecine esthétique et techniques de rajeunissement facial.',
      lecturesPara2: 'Son expérience de plus de trois décennies et le développement de ses propres techniques comme <strong>NewLip®</strong> et <strong>Rhinomodelage®</strong> ont fait de lui une référence de formation pour les professionnels de toute l\'Europe et d\'Amérique latine.',
      lecturesPara3: 'Directeur de la <strong>NewLip Academy</strong>, où il dispense une formation spécialisée aux médecins de diverses disciplines sur les techniques avancées de médecine esthétique faciale.',
      clinicCenters: 'Clinique et Centres',
      clinicDesc: 'Le Dr. Norro exerce à Marbella, travaillant dans divers hôpitaux et cliniques de référence sur la Costa del Sol.',
      clinicAlameda: 'Clinique Esthétique Alameda',
      viewAllCenters: 'Voir tous les centres',
      credentials: 'Accréditations',
      accreditations: 'Accréditations et Certifications',
      accreditationsDesc: 'Membre des principales sociétés scientifiques nationales et internationales de chirurgie plastique et médecine esthétique.',
      viewAccreditations: 'Voir les accréditations',
      wantToKnowMore: 'Vous voulez en savoir plus?',
      requestConsultation: 'Demandez une consultation d\'évaluation personnalisée avec le Dr. Norro.',
      requestConsult: 'Demander une consultation',
    },
    pt: {
      eyebrow: 'Sobre o Dr. Norro',
      heroTitle1: 'Mais de três décadas transformando vidas com',
      heroTitle2: 'precisão e arte',
      biography: 'Biografia e Formação',
      bioParagraph1: 'O Dr. Ángel José Norro Ruiz é especialista em Cirurgia Plástica, Estética e Reparadora com mais de <strong>35 anos de trajetória clínica ininterrupta</strong>. Desenvolve sua atividade em Marbella, referência internacional da medicina privada de alto nível no sul da Europa.',
      bioParagraph2: 'Fundador do protocolo <strong>NewLip®</strong> —registrado na OEPM— para rejuvenescimento perioral, e titular da marca <strong>Rinomodelação®</strong>. Combina atividade clínica de alto volume com o desenvolvimento de novas aplicações terapêuticas.',
      bioParagraph3: 'Diretor científico da <strong>Metasigna</strong>, plataforma biotecnológica de oncologia metabólica que desenvolve conjugados farmacológicos cliváveis sob as marcas Clivamed® e Warcliv®.',
      expertise: 'Áreas de Expertise',
      expertiseItems: [
        'Cirurgia facial · Lifting · Blefaroplastia · Rinoplastia · Otoplastia',
        'Cirurgia corporal · Mamas · Abdominoplastia · Pós-bariátrica · Lipoaspiração',
        'Medicina estética avançada · Rinomodelação® · Endolifting · Harmonização',
        'Cirurgia dermatológica e reparadora',
        'Hipnose clínica perioperatória',
        'Avaliação do dano corporal — perícia médico-legal',
      ],
      philosophy: 'Filosofia e Valores',
      quote: '"A cirurgia plástica não é sobre mudar quem você é. É sobre restaurar a harmonia entre como você se sente e como você se vê."',
      philosophyDesc: 'Cada paciente é único. Cada plano cirúrgico é individualizado. O objetivo não é a transformação radical, mas a restauração da harmonia natural que o tempo ou as circunstâncias alteraram.',
      innovation: 'Inovação',
      trademarks: 'Marcas Registradas OEPM',
      newlipDesc: 'Protocolo exclusivo de rejuvenescimento panfacial, perioral ou periocular. Técnica desenvolvida e registrada pelo Dr. Norro para resultados naturais na zona do lábio e terço inferior facial.',
      rinoDesc: 'Técnica não cirúrgica exclusiva para correção do nariz mediante preenchimentos de ácido hialurônico. Pioneiro na Espanha desde 2007. Resultados imediatos, reversíveis, sem anestesia geral.',
      seeMore: 'Ver mais',
      metasigna: 'Metasigna',
      scientificActivity: 'Atividade Científica',
      metasignaDesc1: 'Diretor científico da <strong>Metasigna</strong>, plataforma biotecnológica de oncologia metabólica que desenvolve conjugados farmacológicos cliváveis.',
      metasignaDesc2: 'Desenvolvimento das marcas <strong>Clivamed®</strong> e <strong>Warcliv®</strong> para aplicações terapêuticas de nova geração.',
      teachingTitle: 'Docência e Divulgação',
      lecturesTitle: 'Palestras e Conferências',
      lecturesPara1: 'O Dr. Norro é <strong>palestrante habitual</strong> em congressos nacionais e internacionais de cirurgia plástica, medicina estética e técnicas de rejuvenescimento facial.',
      lecturesPara2: 'Sua experiência de mais de três décadas e o desenvolvimento de técnicas próprias como <strong>NewLip®</strong> e <strong>Rinomodelação®</strong> o tornaram referência formativa para profissionais de toda a Europa e América Latina.',
      lecturesPara3: 'Diretor da <strong>NewLip Academy</strong>, onde ministra formação especializada a médicos de diversas disciplinas em técnicas avançadas de medicina estética facial.',
      clinicCenters: 'Clínica e Centros',
      clinicDesc: 'O Dr. Norro desenvolve sua atividade clínica em Marbella, trabalhando em diversos hospitais e clínicas de referência na Costa del Sol.',
      clinicAlameda: 'Clínica Estética Alameda',
      viewAllCenters: 'Ver todos os centros',
      credentials: 'Credenciais',
      accreditations: 'Credenciamentos e Certificações',
      accreditationsDesc: 'Membro das principais sociedades científicas nacionais e internacionais de cirurgia plástica e medicina estética.',
      viewAccreditations: 'Ver credenciamentos',
      wantToKnowMore: 'Quer saber mais?',
      requestConsultation: 'Solicite uma consulta de avaliação personalizada com o Dr. Norro.',
      requestConsult: 'Solicitar consulta',
    },
  };

  const t = texts[lang] || texts.es;

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-ivory">
      <Header />
      
      {/* Hero */}
      <section className="pt-12 pb-20 bg-ivory">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="fadeRight">
              <p className="eyebrow mb-6">{t.eyebrow}</p>
              <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-6 leading-tight">
                {t.heroTitle1} <span className="text-gold">{t.heroTitle2}</span>
              </h1>
              <div className="w-16 h-px bg-gold mb-8"></div>
            </AnimatedSection>
            <AnimatedSection animation="fadeLeft" className="relative">
              <div className="absolute -top-4 -left-4 w-20 h-20 border-t-2 border-l-2 border-gold"></div>
              <img 
                src="/images/foto-angel-scrubs.jpeg"
                alt="Dr. Ángel Norro"
                className="w-full h-auto"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Biografía */}
      <section id="biografia" className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-4xl">
          <AnimatedSection animation="fadeUp">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 border border-gold flex items-center justify-center">
                <BookOpen className="text-gold" size={20} />
              </div>
              <h2 className="font-serif text-3xl text-charcoal">{t.biography}</h2>
            </div>
            
            <div className="space-y-6 text-text-secondary leading-relaxed">
              <p dangerouslySetInnerHTML={{ __html: t.bioParagraph1 }} />
              <p dangerouslySetInnerHTML={{ __html: t.bioParagraph2.replace('NewLip®', '<strong class="text-gold">NewLip®</strong>').replace('Rinomodelación®', '<strong class="text-gold">Rinomodelación®</strong>') }} />
              <p dangerouslySetInnerHTML={{ __html: t.bioParagraph3 }} />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-20 bg-ivory">
        <div className="container mx-auto px-6 max-w-4xl">
          <AnimatedSection animation="fadeUp">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 border border-gold flex items-center justify-center">
                <Award className="text-gold" size={20} />
              </div>
              <h2 className="font-serif text-3xl text-charcoal">{t.expertise}</h2>
            </div>
            
            <div className="grid gap-4">
              {t.expertiseItems.map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-4 bg-cream border-l-2 border-gold">
                  <span className="text-gold">✦</span>
                  <span className="text-text-secondary">{item}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Filosofía */}
      <section id="filosofia" className="py-20 bg-charcoal">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <AnimatedSection animation="fadeUp">
            <p className="eyebrow text-gold mb-6">{t.philosophy}</p>
            <blockquote className="font-serif text-2xl md:text-3xl text-ivory font-light italic leading-relaxed mb-8">
              {t.quote}
            </blockquote>
            <div className="w-16 h-px bg-gold mx-auto mb-8"></div>
            <p className="text-gold-light/70 max-w-2xl mx-auto">
              {t.philosophyDesc}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Marcas Registradas */}
      <section id="marcas" className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <p className="eyebrow mb-6">{t.innovation}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal">
              {t.trademarks}
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8">
            <AnimatedSection animation="fadeRight">
              <div className="card-luxury p-8">
                <h3 className="font-serif text-2xl text-gold mb-4">NewLip®</h3>
                <p className="text-text-secondary mb-6 leading-relaxed">
                  {t.newlipDesc}
                </p>
                <div className="flex items-center gap-4">
                  <a 
                    href="https://newlip.es" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gold hover:text-gold/70 text-sm flex items-center gap-1"
                  >
                    newlip.es <ExternalLink size={12} />
                  </a>
                  <a 
                    href="https://academy.newlip.es" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gold hover:text-gold/70 text-sm flex items-center gap-1"
                  >
                    academy.newlip.es <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeLeft">
              <div className="card-luxury p-8">
                <h3 className="font-serif text-2xl text-gold mb-4">Rinomodelación®</h3>
                <p className="text-text-secondary mb-6 leading-relaxed">
                  {t.rinoDesc}
                </p>
                <div className="flex items-center gap-4">
                  <a 
                    href="https://rinomodelacion.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gold hover:text-gold/70 text-sm flex items-center gap-1"
                  >
                    rinomodelacion.com <ExternalLink size={12} />
                  </a>
                  <Link 
                    to="/rinomodelacion"
                    className="text-gold hover:text-gold/70 text-sm flex items-center gap-1"
                  >
                    {t.seeMore} <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Metasigna */}
      <section id="metasigna" className="py-20 bg-ivory">
        <div className="container mx-auto px-6 max-w-4xl">
          <AnimatedSection animation="fadeUp">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 border border-gold flex items-center justify-center">
                <Microscope className="text-gold" size={20} />
              </div>
              <div>
                <h2 className="font-serif text-3xl text-charcoal">{t.metasigna}</h2>
                <p className="text-text-muted text-sm">{t.scientificActivity}</p>
              </div>
            </div>
            
            <div className="bg-cream p-8 border-l-2 border-gold">
              <p className="text-text-secondary leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: t.metasignaDesc1 }} />
              <p className="text-text-secondary leading-relaxed" dangerouslySetInnerHTML={{ __html: t.metasignaDesc2 }} />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Ponencias y Conferencias */}
      <section id="ponencias" className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="fadeRight">
              <p className="eyebrow mb-6">{t.teachingTitle}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-charcoal mb-6">
                {t.lecturesTitle}
              </h2>
              <div className="w-16 h-px bg-gold mb-8"></div>
              <div className="space-y-4 text-text-secondary leading-relaxed">
                <p dangerouslySetInnerHTML={{ __html: t.lecturesPara1 }} />
                <p dangerouslySetInnerHTML={{ __html: t.lecturesPara2 }} />
                <p dangerouslySetInnerHTML={{ __html: t.lecturesPara3 }} />
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fadeLeft" className="relative">
              <div className="absolute -top-4 -right-4 w-20 h-20 border-t-2 border-r-2 border-gold"></div>
              <div className="absolute -bottom-4 -left-4 w-20 h-20 border-b-2 border-l-2 border-gold"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_norro-rebranding/artifacts/2rp0ltof_ntfocpjt_2591c295-28d6-4511-88bf-894a2bdc90f1.jpeg"
                alt="Dr. Ángel Norro en ponencia"
                className="w-full h-auto"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Clínica y Centros */}
      <section id="clinica" className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-4xl">
          <AnimatedSection animation="fadeUp">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 border border-gold flex items-center justify-center">
                <MapPin className="text-gold" size={20} />
              </div>
              <h2 className="font-serif text-3xl text-charcoal">{t.clinicCenters}</h2>
            </div>
            
            <p className="text-text-secondary leading-relaxed mb-8">
              {t.clinicDesc}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/clinica-alameda"
                className="btn-gold inline-flex items-center justify-center gap-2"
              >
                {t.clinicAlameda}
                <ArrowRight size={16} />
              </Link>
              <Link 
                to="/donde-atendemos"
                className="btn-outline-gold inline-flex items-center justify-center gap-2"
              >
                {t.viewAllCenters}
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Acreditaciones CTA */}
      <section className="py-20 bg-charcoal">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <AnimatedSection animation="fadeUp">
            <p className="eyebrow text-gold mb-6">{t.credentials}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-ivory mb-6">
              {t.accreditations}
            </h2>
            <p className="text-gold-light/70 mb-10">
              {t.accreditationsDesc}
            </p>
            <Link 
              to="/el-doctor/acreditaciones"
              className="btn-gold inline-flex items-center gap-2 group"
            >
              {t.viewAccreditations}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <AnimatedSection animation="fadeUp">
            <h3 className="font-serif text-3xl text-charcoal mb-4">
              {t.wantToKnowMore}
            </h3>
            <p className="text-text-secondary mb-10">
              {t.requestConsultation}
            </p>
            <Link 
              to="/contacto"
              className="btn-gold inline-flex items-center gap-2 group"
            >
              {t.requestConsult}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ElDoctor;
